import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import * as api from './endpoints';
import type {
  AuditFilter,
  CaseDetail,
  LabPartnerRow,
  Ref,
  Side,
  UserRoleFilter,
  UserStatusFilter,
  UUID,
} from './types';

export const qk = {
  me: ['me'] as const,
  cases: (p: api.CaseListParams) => ['cases', p] as const,
  stats: (side: Side, org?: UUID) => ['stats', side, org ?? 'all'] as const,
  case: (id: UUID) => ['case', id] as const,
  messages: (id: UUID) => ['messages', id] as const,
  patients: (org?: UUID, q?: string) => ['patients', org ?? 'all', q ?? ''] as const,
  partners: (org: UUID) => ['partners', org] as const,
  members: (org: UUID) => ['members', org] as const,
  users: (role: UserRoleFilter, status: UserStatusFilter, q: string) =>
    ['admin', 'users', role, status, q] as const,
  user: (id: UUID) => ['admin', 'user', id] as const,
  orgs: ['admin', 'orgs'] as const,
  audit: (filter: AuditFilter) => ['admin', 'audit', filter] as const,
};

/* ---------- Кейсы ---------- */

export const useCases = (p: api.CaseListParams, enabled = true) =>
  useQuery({
    queryKey: qk.cases(p),
    queryFn: ({ signal }) => api.listAllCases(p, signal),
    enabled,
    placeholderData: (prev) => prev,
  });

export const useCaseStats = (side: Side, org?: UUID, enabled = true) =>
  useQuery({
    queryKey: qk.stats(side, org),
    queryFn: ({ signal }) => api.getCaseStats(side, org, signal),
    enabled,
    refetchInterval: 60_000,
  });

export const useCase = (id: UUID) =>
  useQuery({
    queryKey: qk.case(id),
    queryFn: ({ signal }) => api.getCase(id, signal),
    // Пока воркер строит превью, опрашиваем кейс.
    refetchInterval: (q) => (q.state.data?.files.some((f) => f.state === 'pending') ? 3000 : false),
  });

export const useMessages = (id: UUID) =>
  useQuery({
    queryKey: qk.messages(id),
    queryFn: ({ signal }) => api.listMessages(id, signal).then((r) => r.items),
    refetchInterval: 20_000,
  });

/** После любого изменения кейса: новая карточка в кэш, списки и счётчики — перечитать. */
export function applyCase(qc: QueryClient, detail: CaseDetail): void {
  qc.setQueryData(qk.case(detail.id), detail);
  void qc.invalidateQueries({ queryKey: ['cases'] });
  void qc.invalidateQueries({ queryKey: ['stats'] });
  void qc.invalidateQueries({ queryKey: qk.messages(detail.id) });
  void qc.invalidateQueries({ queryKey: ['patients'] });
}

export function useCaseMutation<V>(
  id: UUID,
  fn: (vars: V) => Promise<CaseDetail>,
  opts: { manualError?: boolean } = {},
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    meta: { manualError: opts.manualError },
    onSuccess: (detail) => applyCase(qc, detail),
    onError: () => void qc.invalidateQueries({ queryKey: qk.case(id) }),
  });
}

export function usePostMessage(id: UUID, side?: 'doctor' | 'lab') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => api.postMessage(id, text, side),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.messages(id) }),
  });
}

export function useDeleteFile(caseId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fileId: UUID) => api.deleteFile(caseId, fileId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.case(caseId) });
      void qc.invalidateQueries({ queryKey: ['cases'] });
    },
  });
}

/* ---------- Пациенты, партнёры, участники ---------- */

export const usePatients = (org?: UUID, q?: string, enabled = true) =>
  useQuery({
    queryKey: qk.patients(org, q),
    queryFn: ({ signal }) => api.listAllPatients({ org, q }, signal),
    enabled,
    placeholderData: (prev) => prev,
  });

export const useClinicPartners = (clinicId: UUID | undefined) =>
  useQuery({
    queryKey: qk.partners(clinicId ?? ''),
    queryFn: ({ signal }) => api.getClinicPartners(clinicId!, signal).then((r) => r.items.map((x) => x.org)),
    enabled: !!clinicId,
  });

export interface AggregatedPartner extends LabPartnerRow {
  labs: Ref[];
}

/** Клиники-партнёры нескольких лабораторий контекста, сведённые по клинике. */
export function useLabPartners(labs: Ref[]) {
  return useQueries({
    queries: labs.map((lab) => ({
      queryKey: qk.partners(lab.id),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.getLabPartners(lab.id, signal).then((r) => r.items),
    })),
    combine: (results) => {
      const map = new Map<string, AggregatedPartner>();
      results.forEach((r, i) => {
        for (const row of r.data ?? []) {
          const cur = map.get(row.org.id);
          if (!cur) {
            map.set(row.org.id, { ...row, doctors: [...row.doctors], labs: [labs[i]] });
            continue;
          }
          for (const d of row.doctors) if (!cur.doctors.some((x) => x.id === d.id)) cur.doctors.push(d);
          cur.in_work += row.in_work;
          cur.waiting_doctor += row.waiting_doctor;
          cur.late += row.late;
          cur.total += row.total;
          cur.labs.push(labs[i]);
        }
      });
      return {
        rows: [...map.values()].sort((a, b) => a.org.name.localeCompare(b.org.name, 'ru')),
        isLoading: results.some((r) => r.isLoading),
        error: results.find((r) => r.error)?.error ?? null,
      };
    },
  });
}

export const useMembers = (orgId: UUID | undefined) =>
  useQuery({
    queryKey: qk.members(orgId ?? ''),
    queryFn: ({ signal }) => api.getMembers(orgId!, signal).then((r) => r.items),
    enabled: !!orgId,
    staleTime: 60_000,
  });

/* ---------- Админка ---------- */

export const useAdminUsers = (role: UserRoleFilter, status: UserStatusFilter, q: string) =>
  useInfiniteQuery({
    queryKey: qk.users(role, status, q),
    queryFn: ({ pageParam, signal }) => api.listUsers({ role, status, q }, pageParam, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: (prev) => prev,
  });

export const useAdminUser = (id: UUID | null) =>
  useQuery({
    queryKey: qk.user(id ?? ''),
    queryFn: ({ signal }) => api.getUser(id!, signal),
    enabled: !!id,
  });

export const useAdminOrgs = () =>
  useQuery({
    queryKey: qk.orgs,
    queryFn: ({ signal }) => api.listOrgs(signal).then((r) => r.items),
  });

export const useAudit = (filter: AuditFilter) =>
  useInfiniteQuery({
    queryKey: qk.audit(filter),
    queryFn: ({ pageParam, signal }) => api.listAudit(filter, pageParam, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
